import React, { useEffect, useState } from 'react';
import * as Y from 'yjs';

interface FormulaBarProps {
  activeCell: string | null;
  cellsMap: Y.Map<string>;
  computedValue: any;
  isReadOnly?: boolean;
}

export const FormulaBar: React.FC<FormulaBarProps> = ({ activeCell, cellsMap, computedValue, isReadOnly = false }) => {
  // We use state to force a re-render when the Yjs map updates externally
  const [, setVersion] = useState(0);

  useEffect(() => {
    const handleUpdate = () => setVersion(v => v + 1);
    cellsMap.observe(handleUpdate);
    return () => cellsMap.unobserve(handleUpdate);
  }, [cellsMap]);

  const rawValue = activeCell ? (cellsMap.get(activeCell) || '') : '';

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (isReadOnly) return;
    if (activeCell) {
      if (e.target.value === '') {
        cellsMap.delete(activeCell); // Delete if empty to match grid logic
      } else {
        cellsMap.set(activeCell, e.target.value);
      }
    }
  };

  return (
    <div className="bg-gray-100 border-b border-gray-300 p-2 flex items-center space-x-2 shrink-0">
      {/* Cell Address Indicator */}
      <div className="bg-white border border-gray-300 px-3 py-1 rounded min-w-[80px] text-center font-bold text-green-700 shadow-sm">
        {activeCell || '---'}
      </div>
      
      {/* Function Symbol */}
      <div className="text-gray-400 font-serif italic text-xl px-1 select-none">ƒx</div>

      {/* Main Input Area */}
      <div className="flex-grow flex items-center bg-white border border-gray-300 rounded shadow-sm overflow-hidden">
        <input
          type="text"
          value={rawValue}
          onChange={handleChange}
          disabled={!activeCell || isReadOnly}
          placeholder={isReadOnly ? "Read-Only Mode Active" : (activeCell ? "Enter value or formula (e.g. =A1+B1)" : "Select a cell to edit")}
          className="w-full px-3 py-1 outline-none font-mono text-sm text-gray-700"
        />
      </div>

      {/* Quick Result Preview */}
      <div className="hidden md:block text-xs font-medium text-gray-400 px-2 truncate max-w-[200px]">
        {rawValue.startsWith('=') ? `Result: ${computedValue ?? '...'}` : ''}
      </div>
    </div>
  );
};

export default FormulaBar;